// Fills the Figma-exported label template (resources/label-template.svg):
// two colour-keyed image slots + five {marker} text fields. Mirrors the design's
// centering/wrapping so swapped-in values sit where the placeholders did.
import qrcode from 'qrcode-generator';

export interface LabelFields {
  name: string;
  style: string;
  alk: string;
  ibu: string;
  og: string;
  date: string; // bottling date, already formatted for print
  contains: string;
  url: string; // encoded into the QR slot
  artwork: string | null; // data URI from the image generator, or null
  accent: 'black' | 'white'; // recolors the #00E5FF accent elements
}

// Accent marker is #00E5FF, but Figma drifts it by a value or two per element
// (#00E6FF etc.), so match the bright-cyan family. Tight enough to miss the
// sky-blue label; the artwork is a raster image so it's unaffected regardless.
const ACCENT_RE = /#00E[0-9A-F]FF/gi;

const ARTWORK_SLOT = '#EE00FF';
const QR_SLOT = '#00FF0D';
const CONTAINS_RUN = 2800; // line length before {contains} wraps (template units)
const WF = 0.5; // rough glyph advance as a fraction of font-size (Josefin bold)

const est = (s: string, size: number) => s.length * WF * size;

// Advance for the big rotated title. Wider than WF: it is set in caps at a large
// size, where the rough half-em estimate falls short.
// ponytail: eyeballed off a rendered label. If a long name still spills past the
// border, this is the single number to raise.
const TITLE_WF = 0.7;
const xml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

let templateCache: string | null = null;
async function loadTemplate(): Promise<string> {
  if (templateCache === null) {
    const res = await fetch('/resources/label-template.svg');
    if (!res.ok) throw new Error('Failed to load label template');
    templateCache = await res.text();
  }
  return templateCache;
}

function swapImage(svg: string, color: string, href: string): string {
  const re = new RegExp(`<rect ([^>]*?)fill="${color}"\\s*/>`);
  if (!re.test(svg)) throw new Error(`image slot ${color} not found`);
  return svg.replace(
    re,
    (_m, attrs) =>
      `<image ${attrs} preserveAspectRatio="xMidYMid slice" href="${href}"/>`
  );
}

interface Field {
  textOpen: string;
  size: number;
  tspanOpen: string;
  x: number;
  y: number;
}

function fillField(svg: string, marker: string, rewrite: (f: Field) => string): string {
  const inner = marker.slice(1, -1);
  const re = new RegExp(
    `(<text[^>]*font-size="(\\d+)"[^>]*>)(<tspan[^>]*x="([-0-9.]+)"[^>]*y="([-0-9.]+)"[^>]*>)\\{${inner}\\}(</tspan></text>)`
  );
  if (!re.test(svg)) throw new Error(`marker ${marker} not found`);
  return svg.replace(re, (_m, textOpen, size, tspanOpen, x, y) =>
    rewrite({ textOpen, size: +size, tspanOpen, x: +x, y: +y })
  );
}

// Figma baked the centering into the placeholder's x (for its width). Re-anchor
// to the true box center so any value re-centers itself.
function fillCentered(svg: string, marker: string, value: string): string {
  return fillField(svg, marker, ({ textOpen, size, x, y }) => {
    const center = (x + est(marker, size) / 2).toFixed(2);
    return `${textOpen.replace('>', ' text-anchor="middle">')}<tspan x="${center}" y="${y}">${xml(value)}</tspan></text>`;
  });
}

// Right-aligned: pin the value's right edge where the placeholder's ended, so a
// longer or shorter value grows leftwards instead of drifting.
function fillRight(svg: string, marker: string, value: string): string {
  return fillField(svg, marker, ({ textOpen, size, x, y }) => {
    const right = (x + est(marker, size)).toFixed(2);
    return `${textOpen.replace('>', ' text-anchor="end">')}<tspan x="${right}" y="${y}">${xml(value)}</tspan></text>`;
  });
}

// The label's inner border — the one stroked rect in the template — is what the
// vertical title is centred against.
const FRAME_RE = /<rect x="[\d.]+" y="([\d.]+)" width="[\d.]+" height="([\d.]+)" stroke="white"/;

// {name} is the label's big rotated title. It is centred on the frame rather
// than on the placeholder's estimated width (that estimate is off by enough to
// read as a visible shift), and shrinks when a long name would leave the strip.
// The baseline stays put, so the title keeps its alignment against the border.
function fillTitle(svg: string, marker: string, value: string): string {
  const frame = FRAME_RE.exec(svg);
  return fillField(svg, marker, ({ textOpen, size, x, y }) => {
    // Rotated text runs from the transform's origin back towards 0, so a point
    // on the label maps to `origin - y`, and the room on the shorter side of
    // centre is what bounds the title.
    const runTo = +(/translate\([-0-9.]+ ([-0-9.]+)\)/.exec(textOpen)?.[1] ?? 0);
    const center = frame
      ? runTo - (+frame[1] + +frame[2] / 2)
      : x + est(marker, size) / 2;
    const room = 2 * Math.min(center, runTo - center);
    const width = value.length * TITLE_WF * size;
    const fitted = width > room ? Math.floor((room / width) * size) : size;
    return `${textOpen
      .replace(`font-size="${size}"`, `font-size="${fitted}"`)
      .replace('>', ' text-anchor="middle">')}<tspan x="${center.toFixed(2)}" y="${y}">${xml(value)}</tspan></text>`;
  });
}

function fillLeft(svg: string, marker: string, value: string): string {
  return fillField(
    svg,
    marker,
    ({ textOpen, tspanOpen }) => `${textOpen}${tspanOpen}${xml(value)}</tspan></text>`
  );
}

const TEXT_POS = /<text transform="translate\((\d+) (\d+)\)/g;
const LINE_X = /x1="([\d.]+)"/;

// The ALK | IBU | OG row is three columns split by two accent divider lines (the
// only <line> elements in the template). An empty stat takes its value, its
// caption and the divider on its left out with it, then everything to its right
// slides over by one column so the row closes up instead of leaving a hole.
function dropStat(svg: string, marker: string, caption: string): string {
  const inner = marker.slice(1, -1);
  const valueRe = new RegExp(
    `<text transform="translate\\((\\d+) (\\d+)\\)[^>]*>(<tspan[^>]*>)?\\{${inner}\\}</tspan></text>`
  );
  const captionRe = new RegExp(
    `<text transform="translate\\((\\d+) (\\d+)\\)[^>]*><tspan[^>]*>${caption}</tspan></text>`
  );
  const value = valueRe.exec(svg);
  const title = captionRe.exec(svg);
  if (!value || !title) throw new Error(`stat column ${marker} not found`);

  // The column's own two lines of text bound the row band; anything else on the
  // label (the name, the ingredients) sits well outside it and stays put.
  const ys = [+value[2], +title[2]];
  const band = [Math.min(...ys) - 50, Math.max(...ys) + 50];
  const columnX = Math.min(+value[1], +title[1]);

  svg = svg.replace(valueRe, '').replace(captionRe, '');

  const xOf = (line: string) => +(LINE_X.exec(line)?.[1] ?? -1);
  const dividers = svg.match(/<line[^>]*\/>/g) || [];
  const left = dividers.filter((l) => xOf(l) < columnX).sort((a, b) => xOf(b) - xOf(a))[0];
  if (left) svg = svg.replace(left, '');

  // Slide the columns to the right over by the gap this one leaves behind.
  const inRow = (x: number, y: number) => y >= band[0] && y <= band[1] && x > columnX;
  const rights: number[] = [];
  svg.replace(TEXT_POS, (m, x, y) => (inRow(+x, +y) && rights.push(+x), m));
  if (!rights.length) return svg;
  const shift = Math.min(...rights) - columnX;

  svg = svg.replace(TEXT_POS, (m, x, y) =>
    inRow(+x, +y) ? `<text transform="translate(${+x - shift} ${y})` : m
  );
  return svg.replace(/<line[^>]*\/>/g, (l) =>
    xOf(l) > columnX
      ? l.replace(/x1="([\d.]+)"/, (_m, v) => `x1="${+v - shift}"`)
         .replace(/x2="([\d.]+)"/, (_m, v) => `x2="${+v - shift}"`)
      : l
  );
}

// {contains} is a free-length field in a fixed column: word-wrap into stacked lines.
function fillContains(svg: string, value: string): string {
  return fillField(svg, '{contains}', ({ textOpen, size, x, y }) => {
    const maxChars = Math.floor(CONTAINS_RUN / (WF * size));
    const lineH = (size * 1.15).toFixed(1);
    const lines: string[] = [];
    let cur = '';
    for (const w of value.split(' ')) {
      if (cur && (cur + ' ' + w).length > maxChars) {
        lines.push(cur);
        cur = w;
      } else {
        cur = cur ? cur + ' ' + w : w;
      }
    }
    if (cur) lines.push(cur);
    const tspans = lines
      .map((ln, i) =>
        i === 0
          ? `<tspan x="${x}" y="${y}">${xml(ln)}</tspan>`
          : `<tspan x="${x}" dy="${lineH}">${xml(ln)}</tspan>`
      )
      .join('');
    return `${textOpen}${tspans}</text>`;
  });
}

function qrDataUrl(text: string): string {
  const qr = qrcode(0, 'M');
  qr.addData(text);
  qr.make();
  return qr.createDataURL(16, 0);
}

async function buildSvg(fields: LabelFields, embedFonts: boolean): Promise<string> {
  let svg = await loadTemplate();
  // Josefin Sans has no Cyrillic; add Inter as fallback so Russian text renders.
  svg = svg.split('font-family="Josefin Sans"').join('font-family="Josefin Sans, Inter"');
  // Recolor the accent elements (fill + stroke) to the chosen black/white.
  svg = svg.replace(ACCENT_RE, fields.accent === 'white' ? '#FFFFFF' : '#000000');

  if (fields.artwork) svg = swapImage(svg, ARTWORK_SLOT, fields.artwork);
  if (fields.url.trim()) svg = swapImage(svg, QR_SLOT, qrDataUrl(fields.url.trim()));

  svg = fillTitle(svg, '{name}', fields.name);
  svg = fillCentered(svg, '{style}', fields.style);
  svg = fillLeft(svg, '{alk}', fields.alk);
  svg = fields.ibu.trim() ? fillCentered(svg, '{ibu}', fields.ibu) : dropStat(svg, '{ibu}', 'IBU');
  svg = fields.og.trim() ? fillCentered(svg, '{og}', fields.og) : dropStat(svg, '{og}', 'OG');
  svg = fillRight(svg, '{date}', fields.date);
  svg = fillContains(svg, fields.contains);

  if (embedFonts) {
    const css = await fontFaceCss();
    svg = svg.replace(/(<svg[^>]*>)/, `$1<defs><style>${css}</style></defs>`);
  }
  return svg;
}

export async function renderPreview(container: HTMLElement, fields: LabelFields): Promise<void> {
  const svg = await buildSvg(fields, false); // preview uses page webfonts
  container.innerHTML = svg;
  const el = container.querySelector('svg');
  if (el) {
    el.removeAttribute('width');
    el.removeAttribute('height');
    el.style.width = '100%';
    el.style.height = 'auto';
    el.style.display = 'block';
  }
}

export async function exportPng(fields: LabelFields, filename = 'label.png'): Promise<void> {
  const svg = await buildSvg(fields, true); // export embeds fonts for fidelity
  const [, w, h] = /<svg[^>]*width="(\d+)"[^>]*height="(\d+)"/.exec(svg) || [];
  const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }));
  try {
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Failed to rasterize label'));
      img.src = url;
    });
    const canvas = document.createElement('canvas');
    canvas.width = +w || img.naturalWidth;
    canvas.height = +h || img.naturalHeight;
    canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
    const png = await new Promise<Blob>((resolve) =>
      canvas.toBlob((b) => resolve(b!), 'image/png')
    );
    const a = document.createElement('a');
    a.href = URL.createObjectURL(png);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  } finally {
    URL.revokeObjectURL(url);
  }
}

let fontCssCache: string | null = null;
async function fontFaceCss(): Promise<string> {
  if (fontCssCache !== null) return fontCssCache;
  const b64 = async (path: string) => {
    const bytes = new Uint8Array(await (await fetch(path)).arrayBuffer());
    let bin = '';
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    return btoa(bin);
  };
  const [jl, il, ic] = await Promise.all([
    b64('/resources/fonts/josefin-700-latin.woff2'),
    b64('/resources/fonts/inter-700-latin.woff2'),
    b64('/resources/fonts/inter-700-cyrillic.woff2'),
  ]);
  const face = (family: string, data: string, range?: string) =>
    `@font-face{font-family:'${family}';font-weight:700;font-style:normal;` +
    `src:url(data:font/woff2;base64,${data}) format('woff2');` +
    (range ? `unicode-range:${range};` : '') +
    '}';
  fontCssCache =
    face('Josefin Sans', jl) +
    face('Inter', il, 'U+0000-024F,U+2000-206F,U+2116') +
    face('Inter', ic, 'U+0400-04FF');
  return fontCssCache;
}
